import navigationConfig from "configs/NavigationConfig";
import { APP_PREFIX_PATH } from "configs/AppConfig";

/**
 * Funções puras da telemetria de uso (sem dependência de firebase/axios),
 * extraídas para permitir teste unitário isolado.
 */

// --- Mapa path -> viewKey a partir da NavigationConfig ----------------------
const viewKeyByPath: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  const walk = (items: any[]) => {
    items.forEach((item) => {
      if (item?.path && item?.key) map[item.path] = item.key;
      if (item?.submenu?.length) walk(item.submenu);
    });
  };
  walk(navigationConfig);
  return map;
})();

/** Resolve a chave de feature (viewKey) para um pathname, com match por prefixo. */
export function resolveViewKey(pathname: string): string {
  if (viewKeyByPath[pathname]) return viewKeyByPath[pathname];

  const match = Object.keys(viewKeyByPath)
    .filter((p) => pathname.startsWith(p + "/"))
    .sort((a, b) => b.length - a.length)[0];

  // O prefixo raiz ("home") casa com qualquer rota da aplicação; nesse caso o
  // fallback (path sem o prefixo) é mais útil do que atribuir tudo a "home".
  if (match && match !== APP_PREFIX_PATH) return viewKeyByPath[match];
  return pathname.replace(`${APP_PREFIX_PATH}/`, "") || "unknown";
}

// --- Redação de dados sensíveis ---------------------------------------------
// Match por substring (fail-closed): pega também "newPassword", "userToken",
// "confirmSenha" etc. Redigir a mais é aceitável; vazar não é.
const SENSITIVE_KEY_PATTERNS = [
  "password", "senha", "token", "authorization", "jwt",
  "secret", "cardnumber", "cvv", "recoverycode", "apikey",
];

/** Redige recursivamente valores sensíveis mantendo a estrutura (não muta o original). */
export function redactSensitive(value: any, depth = 0): any {
  if (value == null) return value;
  if (depth > 5) return typeof value === "object" ? "[REDACTED_DEPTH_LIMIT]" : value;
  if (typeof FormData !== "undefined" && value instanceof FormData) return "[FormData]";
  if (Array.isArray(value)) return value.map((v) => redactSensitive(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, any> = {};
    for (const [key, val] of Object.entries(value)) {
      const lower = key.toLowerCase();
      out[key] = SENSITIVE_KEY_PATTERNS.some((pattern) => lower.includes(pattern))
        ? "[REDACTED]"
        : redactSensitive(val, depth + 1);
    }
    return out;
  }
  return value;
}

// --- Sanitização para GA4 ----------------------------------------------------
/** GA4: nomes de parâmetro <=40 chars, valores string <=100, máx ~25 params. */
export function sanitizeGa4Params(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  let count = 0;
  for (const [key, value] of Object.entries(params || {})) {
    if (value == null || count >= 24) continue;
    const k = key.slice(0, 40);
    if (typeof value === "number" || typeof value === "boolean") {
      out[k] = value;
    } else if (typeof value === "string") {
      out[k] = value.slice(0, 100);
    } else {
      continue; // ignora objetos aninhados
    }
    count++;
  }
  return out;
}

// --- Formatação --------------------------------------------------------------
export function humanizeDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
